import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import { TwoActionsDialogService } from '../../../../services/twoActionsDialog/twoActionsDialogService';
import { WizardUtils } from '../../../../utils/WizardUtils';
import EditableTable from '../../components/EditableTable';
import GenericAutoCompleteInput from '../../components/GenericAutoCompleteInput';
import { filterSortRowsByAction, getSearchTokens } from './SequencesActionsTab.utils';
import { useSequenceActions } from './hooks/SequenceActionsHook';

const PAGE_SIZE = 20;
const IMPACT_TYPES = [
  'NoAction',
  'Repairs',
  'Replacement',
  'Inspection',
  'Activation',
  'Deactivation',
  'RepairsWithoutInspection',
  'Refurbishment',
];

const filterVisibleColumns = (columns = []) => columns.filter((col) => col?.isInvisible !== true);
const clampPage = (page, totalPages) => Math.min(page, Math.max(totalPages - 1, 0));
const hasSearchInput = (includeValue, excludeValue) => includeValue.trim().length > 0 || excludeValue.trim().length > 0;

const useDebouncedValue = (value, delay = 180) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timeoutId);
  }, [delay, value]);

  return debouncedValue;
};

const SequencesActionsTab = ({ selectedSequenceAction, setSelectedSequenceAction, selectedGisement, editMode }) => {
  const { control, setValue, getValues } = useFormContext();
  const {
    sequencesActions,
    coutsEtDurees,
    impacts,
    familleEquipement,
    niveauCout,
    tableNamesMapping,
    categoriesBudgetaires,
    tachesEnIntervention,
    tachesEnInterventionTable,
  } = useSequenceActions(selectedGisement);

  const { field: sequencesActionsField } = useController({
    name: tableNamesMapping.SequencesActions,
    control,
    defaultValue: sequencesActions ?? [],
  });
  const { field: coutsEtDureesField } = useController({
    name: tableNamesMapping.CoutsEtDureesActions,
    control,
    defaultValue: coutsEtDurees ?? [],
  });
  const { field: impactsField } = useController({
    name: tableNamesMapping.Impacts,
    control,
    defaultValue: impacts ?? [],
  });

  const sequencesActionsValue = useMemo(
    () => sequencesActionsField.value ?? sequencesActions ?? [],
    [sequencesActions, sequencesActionsField.value]
  );
  const coutsEtDureesValue = useMemo(
    () => coutsEtDureesField.value ?? coutsEtDurees ?? [],
    [coutsEtDurees, coutsEtDureesField.value]
  );
  const impactsValue = useMemo(() => impactsField.value ?? impacts ?? [], [impacts, impactsField.value]);

  const [openCoutDureeModal, setOpenCoutDureeModal] = useState(false);
  const [openImpactModal, setOpenImpactModal] = useState(false);
  const [openTacheEnInterventionModal, setOpenTacheEnInterventionModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState('');
  const [impactSearchText, setImpactSearchText] = useState('');
  const [impactExcludeText, setImpactExcludeText] = useState('');
  const [selectedImpactIds, setSelectedImpactIds] = useState([]);
  const [bulkType, setBulkType] = useState('');
  const [bulkImpact, setBulkImpact] = useState('');
  const [bulkImpactPriorisation, setBulkImpactPriorisation] = useState(false);
  const [impactPage, setImpactPage] = useState(0);
  const [coutDureePage, setCoutDureePage] = useState(0);
  const [coutSearchText, setCoutSearchText] = useState('');
  const [coutExcludeText, setCoutExcludeText] = useState('');

  const debouncedImpactSearchText = useDebouncedValue(impactSearchText);
  const debouncedImpactExcludeText = useDebouncedValue(impactExcludeText);
  const debouncedCoutSearchText = useDebouncedValue(coutSearchText);
  const debouncedCoutExcludeText = useDebouncedValue(coutExcludeText);

  const impactIncludeTokens = useMemo(() => getSearchTokens(debouncedImpactSearchText), [debouncedImpactSearchText]);
  const impactExcludeTokens = useMemo(() => getSearchTokens(debouncedImpactExcludeText), [debouncedImpactExcludeText]);
  const coutIncludeTokens = useMemo(() => getSearchTokens(debouncedCoutSearchText), [debouncedCoutSearchText]);
  const coutExcludeTokens = useMemo(() => getSearchTokens(debouncedCoutExcludeText), [debouncedCoutExcludeText]);

  const hasImpactSearchInput = hasSearchInput(impactSearchText, impactExcludeText);
  const hasCoutSearchInput = hasSearchInput(coutSearchText, coutExcludeText);
  const isImpactFilteringActive = impactIncludeTokens.length > 0 || impactExcludeTokens.length > 0;

  const updateFieldValue = useCallback(
    (fieldName, value) => {
      setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    },
    [setValue]
  );

  const sequenceActionOptions = useMemo(() => {
    const sequences = sequencesActionsValue.map((sa) => sa.SequenceActions).filter(Boolean);
    return WizardUtils.sortResourceListAlphabetically(Array.from(new Set(sequences)));
  }, [sequencesActionsValue]);

  const selectedAction = useMemo(
    () => sequencesActionsValue.find((action) => action.IdentifiantAction === selectedActionId),
    [selectedActionId, sequencesActionsValue]
  );
  const niveauDefinitionCout = selectedAction?.NiveauDefinitionCouts ?? '';

  const resetImpactSearch = useCallback(() => {
    setImpactSearchText('');
    setImpactExcludeText('');
    setSelectedImpactIds([]);
    setBulkType('');
    setBulkImpact('');
    setBulkImpactPriorisation(false);
    setImpactPage(0);
  }, []);

  const openCoutsEtDureesModal = useCallback((actionId) => {
    setSelectedActionId(actionId);
    setOpenCoutDureeModal(true);
    setCoutDureePage(0);
    setCoutSearchText('');
    setCoutExcludeText('');
  }, []);

  const openTachesEnInterventionModal = useCallback((actionId) => {
    setSelectedActionId(actionId);
    setOpenTacheEnInterventionModal(true);
  }, []);

  const openImpactsModal = useCallback(
    (actionId) => {
      setSelectedActionId(actionId);
      setOpenImpactModal(true);
      resetImpactSearch();
    },
    [resetImpactSearch]
  );

  const validateActionId = useCallback((row, allRows) => {
    if (!row.IdentifiantAction || row.IdentifiantAction.trim() === '') {
      return true;
    }

    return allRows?.some(
      (saRow) =>
        saRow.IdentifiantAction === row.IdentifiantAction &&
        saRow.SequenceActions === row.SequenceActions &&
        saRow.id !== row.id
    );
  }, []);

  const parametresColumns = useMemo(
    () =>
      TECHNICAL_POLICY_COLUMNS.SequencesActions(
        openCoutsEtDureesModal,
        openTachesEnInterventionModal,
        openImpactsModal,
        categoriesBudgetaires,
        tachesEnIntervention,
        (row) =>
          sequencesActions?.some((saRow) => saRow.IdentifiantAction === row.IdentifiantAction && saRow.id === row.id),
        validateActionId
      ),
    [
      categoriesBudgetaires,
      openCoutsEtDureesModal,
      openImpactsModal,
      openTachesEnInterventionModal,
      sequencesActions,
      tachesEnIntervention,
      validateActionId,
    ]
  );

  const visibleSequenceColumns = useMemo(() => filterVisibleColumns(parametresColumns), [parametresColumns]);
  const visibleCoutColumns = useMemo(
    () =>
      filterVisibleColumns(
        TECHNICAL_POLICY_COLUMNS.CoutsEtDureesActions(niveauCout, familleEquipement, niveauDefinitionCout)
      ),
    [familleEquipement, niveauCout, niveauDefinitionCout]
  );
  const visibleImpactColumns = useMemo(
    () => filterVisibleColumns(TECHNICAL_POLICY_COLUMNS.Impacts(familleEquipement)),
    [familleEquipement]
  );
  const visibleTachesColumns = useMemo(
    () => filterVisibleColumns(TECHNICAL_POLICY_COLUMNS.TachesEnIntervention(familleEquipement)),
    [familleEquipement]
  );

  const sequenceRows = useMemo(
    () => sequencesActionsValue.filter((action) => action.SequenceActions === selectedSequenceAction),
    [selectedSequenceAction, sequencesActionsValue]
  );

  const filteredCoutRows = useMemo(
    () =>
      filterSortRowsByAction({
        rows: coutsEtDureesValue,
        actionId: selectedActionId,
        searchField: 'FamilleEquipement',
        includeTokens: coutIncludeTokens,
        excludeTokens: coutExcludeTokens,
        sortFields: ['FamilleEquipement', 'NiveauCouts'],
      }),
    [coutExcludeTokens, coutIncludeTokens, coutsEtDureesValue, selectedActionId]
  );

  const needsCoutPagination = niveauDefinitionCout === 'SubAction' && filteredCoutRows.length > PAGE_SIZE;
  const totalCoutPages = needsCoutPagination ? Math.max(1, Math.ceil(filteredCoutRows.length / PAGE_SIZE)) : 1;
  const safeCoutPage = clampPage(coutDureePage, totalCoutPages);
  const displayedCoutRows = useMemo(() => {
    if (!needsCoutPagination) {
      return filteredCoutRows;
    }

    const startIndex = safeCoutPage * PAGE_SIZE;
    return filteredCoutRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredCoutRows, needsCoutPagination, safeCoutPage]);

  const filteredImpactRows = useMemo(
    () =>
      filterSortRowsByAction({
        rows: impactsValue,
        actionId: selectedActionId,
        searchField: 'FamilleEquipementImpactee',
        includeTokens: impactIncludeTokens,
        excludeTokens: impactExcludeTokens,
        sortFields: ['FamilleEquipementImpactee'],
      }),
    [impactExcludeTokens, impactIncludeTokens, impactsValue, selectedActionId]
  );

  const totalDisplayedImpacts = filteredImpactRows.length;
  const totalImpactPages = Math.max(1, Math.ceil(totalDisplayedImpacts / PAGE_SIZE));
  const safeImpactPage = clampPage(impactPage, totalImpactPages);
  const paginatedImpactRows = useMemo(() => {
    const startIndex = safeImpactPage * PAGE_SIZE;
    return filteredImpactRows.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredImpactRows, safeImpactPage]);

  const selectedActionTaskIds = useMemo(() => {
    const taskIds = new Set();

    sequencesActionsValue
      .filter((sequenceAction) => sequenceAction.IdentifiantAction === selectedActionId)
      .forEach((sequenceAction) => {
        (sequenceAction.TachesEnIntervention ?? '')
          .split('|')
          .map((taskId) => taskId.trim())
          .filter(Boolean)
          .forEach((taskId) => taskIds.add(taskId));
      });

    return taskIds;
  }, [selectedActionId, sequencesActionsValue]);

  const filteredTachesRows = useMemo(
    () =>
      (tachesEnInterventionTable ?? [])
        .filter((row) => selectedActionTaskIds.has(row.Identifiant))
        .sort((a, b) => String(a.Identifiant ?? '').localeCompare(String(b.Identifiant ?? ''))),
    [selectedActionTaskIds, tachesEnInterventionTable]
  );

  const selectedImpactIdsSet = useMemo(() => new Set(selectedImpactIds), [selectedImpactIds]);
  const displayedImpactIds = useMemo(() => filteredImpactRows.map((row) => row.id), [filteredImpactRows]);
  const allDisplayedSelected =
    displayedImpactIds.length > 0 && displayedImpactIds.every((impactId) => selectedImpactIdsSet.has(impactId));

  useEffect(() => {
    setCoutDureePage((previousPage) => clampPage(previousPage, totalCoutPages));
  }, [totalCoutPages]);

  useEffect(() => {
    setImpactPage((previousPage) => clampPage(previousPage, totalImpactPages));
  }, [totalImpactPages]);

  const handleNewActionSaved = useCallback(
    (newRow) => {
      if (!newRow?.IdentifiantAction) {
        return;
      }

      const existingImpacts = getValues(tableNamesMapping.Impacts) ?? impactsValue;
      const newImpacts = WizardUtils.generateDefaultImpacts(newRow.IdentifiantAction, familleEquipement);
      updateFieldValue(tableNamesMapping.Impacts, [...existingImpacts, ...newImpacts]);

      const newCouts = WizardUtils.generateDefaultCoutsEtDurees(
        newRow.NiveauDefinitionCouts,
        newRow.IdentifiantAction,
        familleEquipement,
        niveauCout
      );
      const existingCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDureesValue;
      updateFieldValue(tableNamesMapping.CoutsEtDureesActions, [...existingCouts, ...newCouts]);
    },
    [
      coutsEtDureesValue,
      familleEquipement,
      getValues,
      impactsValue,
      niveauCout,
      tableNamesMapping.CoutsEtDureesActions,
      tableNamesMapping.Impacts,
      updateFieldValue,
    ]
  );

  const handleAddSequenceAction = useCallback(
    (newSequenceAction) => {
      setSelectedSequenceAction(newSequenceAction);
    },
    [setSelectedSequenceAction]
  );

  const handleDeleteSequenceAction = useCallback(
    async (event) => {
      if (!selectedSequenceAction) {
        return undefined;
      }

      event.stopPropagation();
      const result = await TwoActionsDialogService.openDialog({
        id: 'delete-sequence-action',
        labels: {
          title: "Supprimer la séquence d'actions ?",
          body:
            'Les actions, coûts et durées ainsi que les impacts associés ' +
            'à cette séquence seront également supprimés.',
          button1: 'Annuler',
          button2: 'Supprimer',
        },
      });

      if (result === 2) {
        setSelectedSequenceAction('');
      }

      return result;
    },
    [selectedSequenceAction, setSelectedSequenceAction]
  );

  const handleActionRowUpdate = useCallback(
    async (newRow, oldRow) => {
      if (newRow.IdentifiantAction && newRow.NiveauDefinitionCouts !== oldRow.NiveauDefinitionCouts) {
        const dialogResult = await TwoActionsDialogService.openDialog({
          id: 'confirm-niveau-definition-change',
          labels: {
            title: 'Changer le niveau de définition ?',
            body:
              'Cette modification entraînera la suppression des Coûts et Durées existants. ' +
              'De nouvelles valeurs par défaut seront générées.',
            button1: 'Annuler',
            button2: 'Continuer',
          },
        });

        if (dialogResult === 2) {
          const newCouts = WizardUtils.generateDefaultCoutsEtDurees(
            newRow.NiveauDefinitionCouts,
            newRow.IdentifiantAction,
            familleEquipement,
            niveauCout
          );
          const allCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDureesValue;
          const filteredCouts = allCouts.filter((row) => row.IdentifiantAction !== oldRow.IdentifiantAction);
          updateFieldValue(tableNamesMapping.CoutsEtDureesActions, [...filteredCouts, ...newCouts]);

          if (oldRow.IdentifiantAction !== newRow.IdentifiantAction) {
            const allImpacts = getValues(tableNamesMapping.Impacts) ?? impactsValue;
            const updatedImpacts = allImpacts.map((row) =>
              row.IdentifiantAction === oldRow.IdentifiantAction
                ? { ...row, IdentifiantAction: newRow.IdentifiantAction }
                : row
            );
            updateFieldValue(tableNamesMapping.Impacts, updatedImpacts);
          }
        } else {
          const allRows = getValues(tableNamesMapping.SequencesActions) ?? sequencesActionsValue;
          const revertedRows = allRows.map((row) =>
            row.id === newRow.id ? { ...row, NiveauDefinitionCouts: oldRow.NiveauDefinitionCouts } : row
          );
          updateFieldValue(tableNamesMapping.SequencesActions, revertedRows);
        }
      } else if (
        oldRow.IdentifiantAction &&
        newRow.IdentifiantAction &&
        oldRow.IdentifiantAction !== newRow.IdentifiantAction
      ) {
        const allCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDureesValue;
        const updatedCouts = allCouts.map((row) =>
          row.IdentifiantAction === oldRow.IdentifiantAction
            ? { ...row, IdentifiantAction: newRow.IdentifiantAction }
            : row
        );
        updateFieldValue(tableNamesMapping.CoutsEtDureesActions, updatedCouts);

        const allImpacts = getValues(tableNamesMapping.Impacts) ?? impactsValue;
        const updatedImpacts = allImpacts.map((row) =>
          row.IdentifiantAction === oldRow.IdentifiantAction
            ? { ...row, IdentifiantAction: newRow.IdentifiantAction }
            : row
        );
        updateFieldValue(tableNamesMapping.Impacts, updatedImpacts);
      }
    },
    [
      coutsEtDureesValue,
      familleEquipement,
      getValues,
      impactsValue,
      niveauCout,
      sequencesActionsValue,
      tableNamesMapping.CoutsEtDureesActions,
      tableNamesMapping.Impacts,
      tableNamesMapping.SequencesActions,
      updateFieldValue,
    ]
  );

  const handleDeleteAction = useCallback(
    (deletedRow) => {
      if (!deletedRow?.IdentifiantAction) {
        return;
      }

      const actionId = deletedRow.IdentifiantAction;
      const currentCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDureesValue;
      const currentImpacts = getValues(tableNamesMapping.Impacts) ?? impactsValue;

      updateFieldValue(
        tableNamesMapping.CoutsEtDureesActions,
        currentCouts.filter((row) => row.IdentifiantAction !== actionId)
      );
      updateFieldValue(
        tableNamesMapping.Impacts,
        currentImpacts.filter((row) => row.IdentifiantAction !== actionId)
      );
    },
    [
      coutsEtDureesValue,
      getValues,
      impactsValue,
      tableNamesMapping.CoutsEtDureesActions,
      tableNamesMapping.Impacts,
      updateFieldValue,
    ]
  );

  const applyImpactTypeLogic = useCallback(
    (newRow, oldRow) => {
      const typeChanged = oldRow && oldRow.Type !== newRow.Type;
      const newImpactPriorisation = typeChanged ? newRow.Type !== 'NoAction' : newRow.ImpactPriorisation;

      if (newRow.Type === 'NoAction') {
        return {
          ...newRow,
          Impact: 0,
          FamilleEquipementRetrofit: null,
          ImpactPriorisation: newImpactPriorisation,
        };
      }

      if (['Inspection', 'Activation', 'Deactivation'].includes(newRow.Type)) {
        return {
          ...newRow,
          Impact: 0,
          FamilleEquipementRetrofit: null,
          ImpactPriorisation: newImpactPriorisation,
        };
      }

      if (newRow.Type === 'Replacement') {
        return {
          ...newRow,
          Impact: 1,
          FamilleEquipementRetrofit: typeChanged ? null : newRow.FamilleEquipementRetrofit,
          ImpactPriorisation: newImpactPriorisation,
        };
      }

      if (newRow.Type === 'Repairs' || newRow.Type === 'RepairsWithoutInspection') {
        return {
          ...newRow,
          FamilleEquipementRetrofit: null,
          ImpactPriorisation: newImpactPriorisation,
        };
      }

      if (newRow.Type === 'Refurbishment') {
        const availableOptions =
          familleEquipement?.filter((option) => option.value !== newRow.FamilleEquipementImpactee) ?? [];
        const firstOption = availableOptions.length > 0 ? availableOptions[0].value : null;
        const shouldResetRetrofit =
          typeChanged ||
          newRow.FamilleEquipementRetrofit === null ||
          newRow.FamilleEquipementRetrofit === '' ||
          newRow.FamilleEquipementRetrofit === newRow.FamilleEquipementImpactee;

        return {
          ...newRow,
          Impact: 0,
          FamilleEquipementRetrofit: shouldResetRetrofit ? firstOption : newRow.FamilleEquipementRetrofit,
          ImpactPriorisation: newImpactPriorisation,
        };
      }

      return { ...newRow, ImpactPriorisation: newImpactPriorisation };
    },
    [familleEquipement]
  );

  const toggleImpactSelection = useCallback((impactId) => {
    setSelectedImpactIds((previousIds) => {
      const nextIds = new Set(previousIds);
      if (nextIds.has(impactId)) {
        nextIds.delete(impactId);
      } else {
        nextIds.add(impactId);
      }
      return Array.from(nextIds);
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedImpactIds((previousIds) => {
      const nextIds = new Set(previousIds);
      const areAllDisplayedSelected =
        displayedImpactIds.length > 0 && displayedImpactIds.every((impactId) => nextIds.has(impactId));

      if (areAllDisplayedSelected) {
        displayedImpactIds.forEach((impactId) => nextIds.delete(impactId));
      } else {
        displayedImpactIds.forEach((impactId) => nextIds.add(impactId));
      }

      return Array.from(nextIds);
    });
  }, [displayedImpactIds]);

  const handleBulkApply = useCallback(() => {
    if (selectedImpactIds.length === 0) {
      return;
    }

    const selectedIdsSet = new Set(selectedImpactIds);
    const updatedValue = impactsValue.map((row) => {
      if (!selectedIdsSet.has(row.id)) {
        return row;
      }

      const updatedRow = { ...row };
      if (bulkType) {
        updatedRow.Type = bulkType;
      }
      if (bulkImpact !== '') {
        const parsedImpact = Math.min(1, Math.max(0, parseFloat(bulkImpact)));
        if (!Number.isNaN(parsedImpact)) {
          updatedRow.Impact = parsedImpact;
        }
      }
      updatedRow.ImpactPriorisation = bulkImpactPriorisation;

      return applyImpactTypeLogic(updatedRow, row);
    });

    impactsField.onChange(updatedValue);
  }, [
    applyImpactTypeLogic,
    bulkImpact,
    bulkImpactPriorisation,
    bulkType,
    impactsField,
    impactsValue,
    selectedImpactIds,
  ]);

  const confirmDeleteDialogLabels = useMemo(
    () => ({
      id: 'delete-actions-sequence',
      labels: {
        title: "Supprimer la séquence d'actions ?",
        body: 'Les coût et durées ainsi que les impacts associés à cette séquence seront également supprimés',
        button1: 'Annuler',
        button2: 'Supprimer',
      },
    }),
    []
  );

  return (
    <Box width="100%" sx={{ padding: 2 }}>
      <Grid container sx={{ gap: 1, marginBottom: 2 }}>
        <Grid item>
          <GenericAutoCompleteInput
            id="select-or-create-actions-sequence-autocomplete"
            label={"Sélectionner ou créer une séquence d'actions"}
            value={selectedSequenceAction || ''}
            options={sequenceActionOptions}
            onChange={(event, newSequenceAction) => setSelectedSequenceAction(newSequenceAction)}
            onNewOption={handleAddSequenceAction}
            editMode={editMode}
          />
        </Grid>
        <Grid item>
          {selectedSequenceAction && editMode && (
            <IconButton
              data-cy="delete-sequence-action-button"
              onClick={async (event) => {
                const response = await handleDeleteSequenceAction(event);
                if (response !== 2) {
                  return;
                }

                const filteredSequencesActions = sequencesActionsValue.filter(
                  (sequenceAction) => sequenceAction.SequenceActions !== selectedSequenceAction
                );
                sequencesActionsField.onChange(filteredSequencesActions);

                const actionIdsToDelete = new Set(
                  sequencesActionsValue
                    .filter((sequenceAction) => sequenceAction.SequenceActions === selectedSequenceAction)
                    .map((sequenceAction) => sequenceAction.IdentifiantAction)
                );

                updateFieldValue(
                  tableNamesMapping.CoutsEtDureesActions,
                  coutsEtDureesValue.filter((row) => !actionIdsToDelete.has(row.IdentifiantAction))
                );
                updateFieldValue(
                  tableNamesMapping.Impacts,
                  impactsValue.filter((row) => !actionIdsToDelete.has(row.IdentifiantAction))
                );
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>

      <EditableTable
        key={selectedSequenceAction}
        id="actions-sequence"
        columns={visibleSequenceColumns}
        rows={sequenceRows}
        onChange={sequencesActionsField.onChange}
        notFilteredRows={sequencesActionsValue}
        filteringCol={{ field: 'SequenceActions', value: selectedSequenceAction }}
        editMode={editMode && !!selectedSequenceAction}
        onDeleteRow={handleDeleteAction}
        confirmDeleteRow={true}
        confirmDeleteRowDialogProps={confirmDeleteDialogLabels}
        onNewRowSaved={handleNewActionSaved}
        addButtonAlignment="left"
        onRowUpdate={handleActionRowUpdate}
      />

      <Dialog maxWidth="xl" fullWidth={true} open={openCoutDureeModal} onClose={() => setOpenCoutDureeModal(false)}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Coûts et Durées</span>
          {niveauDefinitionCout === 'SubAction' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                data-cy="cout-search-family"
                size="small"
                placeholder="Inclure..."
                value={coutSearchText}
                onChange={(event) => {
                  setCoutSearchText(event.target.value);
                  setCoutDureePage(0);
                }}
                sx={{ width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                data-cy="cout-exclude-family"
                size="small"
                placeholder="Exclure..."
                value={coutExcludeText}
                onChange={(event) => {
                  setCoutExcludeText(event.target.value);
                  setCoutDureePage(0);
                }}
                sx={{ width: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CloseIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              {hasCoutSearchInput && (
                <IconButton
                  data-cy="cout-search-clear"
                  size="small"
                  onClick={() => {
                    setCoutSearchText('');
                    setCoutExcludeText('');
                    setCoutDureePage(0);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <EditableTable
            id="cost-and-duration"
            columns={visibleCoutColumns}
            rows={displayedCoutRows}
            onChange={coutsEtDureesField.onChange}
            notFilteredRows={coutsEtDureesValue}
            filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
            editMode={editMode}
            canAddDeleteRows={false}
            inlineEdit={true}
          />
          {needsCoutPagination && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                data-cy="cout-duree-page-prev"
                size="small"
                disabled={safeCoutPage === 0}
                onClick={() => setCoutDureePage((page) => Math.max(0, page - 1))}
              >
                Précédent
              </Button>
              <Typography variant="body2">
                Page {safeCoutPage + 1} / {totalCoutPages} ({filteredCoutRows.length} lignes)
              </Typography>
              <Button
                data-cy="cout-duree-page-next"
                size="small"
                disabled={safeCoutPage >= totalCoutPages - 1}
                onClick={() => setCoutDureePage((page) => Math.min(totalCoutPages - 1, page + 1))}
              >
                Suivant
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-cost-and-duration-dialog-button" onClick={() => setOpenCoutDureeModal(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="xl"
        fullWidth={true}
        open={openTacheEnInterventionModal}
        onClose={() => setOpenTacheEnInterventionModal(false)}
      >
        <DialogTitle>Tâches en Intervention</DialogTitle>
        <DialogContent>
          <EditableTable
            id="taches-en-intervention"
            columns={visibleTachesColumns}
            rows={filteredTachesRows}
            notFilteredRows={tachesEnInterventionTable}
            filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
            onChange={() => {}}
            canAddDeleteRows={false}
            hideActions={true}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-impacts-dialog-button" onClick={() => setOpenTacheEnInterventionModal(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="xl"
        fullWidth={true}
        open={openImpactModal}
        onClose={() => {
          setOpenImpactModal(false);
          resetImpactSearch();
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Impacts</span>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              data-cy="impact-search-family"
              size="small"
              placeholder="Inclure..."
              value={impactSearchText}
              onChange={(event) => {
                setImpactSearchText(event.target.value);
                setSelectedImpactIds([]);
                setImpactPage(0);
              }}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              data-cy="impact-exclude-family"
              size="small"
              placeholder="Exclure..."
              value={impactExcludeText}
              onChange={(event) => {
                setImpactExcludeText(event.target.value);
                setSelectedImpactIds([]);
                setImpactPage(0);
              }}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CloseIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            {hasImpactSearchInput && (
              <IconButton data-cy="impact-search-clear" size="small" onClick={resetImpactSearch}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {isImpactFilteringActive && filteredImpactRows.length > 0 && (
            <Box
              data-cy="impact-bulk-update-panel"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                flexWrap: 'wrap',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    data-cy="impact-select-all-checkbox"
                    checked={allDisplayedSelected}
                    indeterminate={selectedImpactIds.length > 0 && !allDisplayedSelected}
                    onChange={toggleSelectAll}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="bold">
                    Tout sélectionner ({selectedImpactIds.length}/{displayedImpactIds.length})
                  </Typography>
                }
              />
              <Select
                data-cy="impact-bulk-type-select"
                value={bulkType}
                displayEmpty
                size="small"
                onChange={(event) => setBulkType(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">
                  <em>Type...</em>
                </MenuItem>
                {IMPACT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                data-cy="impact-bulk-impact-value"
                size="small"
                type="number"
                placeholder="Impact"
                value={bulkImpact}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  if (nextValue === '') {
                    setBulkImpact('');
                    return;
                  }

                  const parsedValue = parseFloat(nextValue);
                  if (!Number.isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 1) {
                    setBulkImpact(nextValue);
                  }
                }}
                inputProps={{ min: 0, max: 1, step: 0.01 }}
                sx={{ width: 100 }}
                disabled={[
                  'NoAction',
                  'Replacement',
                  'Inspection',
                  'Activation',
                  'Deactivation',
                  'Refurbishment',
                ].includes(bulkType)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    data-cy="impact-bulk-prioritisation-checkbox"
                    checked={bulkImpactPriorisation}
                    onChange={(event) => setBulkImpactPriorisation(event.target.checked)}
                  />
                }
                label={<Typography variant="body2">Impact sur la priorité</Typography>}
              />
              <Button
                data-cy="impact-bulk-apply-button"
                variant="contained"
                size="small"
                disabled={selectedImpactIds.length === 0 || (!bulkType && bulkImpact === '')}
                onClick={handleBulkApply}
              >
                Appliquer ({selectedImpactIds.length})
              </Button>
            </Box>
          )}

          <EditableTable
            id="impacts"
            columns={visibleImpactColumns}
            rows={paginatedImpactRows}
            notFilteredRows={impactsValue}
            filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
            onChange={impactsField.onChange}
            canAddDeleteRows={false}
            inlineEdit={true}
            transformRowBeforeChange={applyImpactTypeLogic}
            leadingColumn={
              isImpactFilteringActive && paginatedImpactRows.length > 0
                ? {
                    width: 42,
                    header: null,
                    renderCell: (row) => (
                      <Checkbox
                        data-cy={`impact-row-checkbox-${row.id}`}
                        checked={selectedImpactIdsSet.has(row.id)}
                        onChange={() => toggleImpactSelection(row.id)}
                        size="small"
                      />
                    ),
                  }
                : null
            }
          />

          {totalDisplayedImpacts > PAGE_SIZE && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
                mt: 2,
              }}
            >
              <Button
                data-cy="impact-page-prev"
                size="small"
                disabled={safeImpactPage === 0}
                onClick={() => setImpactPage((page) => Math.max(0, page - 1))}
              >
                Précédent
              </Button>
              <Typography variant="body2">
                Page {safeImpactPage + 1} / {totalImpactPages} ({totalDisplayedImpacts} lignes)
              </Typography>
              <Button
                data-cy="impact-page-next"
                size="small"
                disabled={safeImpactPage >= totalImpactPages - 1}
                onClick={() => setImpactPage((page) => Math.min(totalImpactPages - 1, page + 1))}
              >
                Suivant
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-cy="close-impacts-dialog-button"
            onClick={() => {
              setOpenImpactModal(false);
              resetImpactSearch();
            }}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

SequencesActionsTab.propTypes = {
  selectedSequenceAction: PropTypes.string,
  setSelectedSequenceAction: PropTypes.func.isRequired,
  selectedGisement: PropTypes.string,
  editMode: PropTypes.bool,
};

export default SequencesActionsTab;
